import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import SemaforoPersone from "@/components/SemaforoPersone";
import ReportCantieri from "@/components/ReportCantieri";
import PrevisioneDpi from "@/components/PrevisioneDpi";

export default function Reportistica() {
  const [groupBy, setGroupBy] = useState("employee");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ group_by: groupBy });
    if (from) params.append("date_from", from);
    if (to) params.append("date_to", to);
    const { data } = await api.get(`/reports/summary?${params.toString()}`);
    setRows(data.results.sort((a,b) => b.total_qty - a.total_qty));
  }, [groupBy, from, to]);
  useEffect(() => { load(); }, [load]);

  const chartData = rows.slice(0, 10).map(r => ({ name: r.label, qty: r.total_qty }));

  return (
    <div className="space-y-6" data-testid="reportistica-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Admin</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Reportistica</h1>
      </div>

      <SemaforoPersone />

      <ReportCantieri />

      <PrevisioneDpi />

      <div>
        <h2 className="font-display text-xl font-bold">Consegne DPI</h2>
        <p className="text-sm text-slate-500">Chi ha ricevuto cosa, nel periodo scelto</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">Raggruppa per Dipendente</SelectItem>
              <SelectItem value="item">Raggruppa per Articolo</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} placeholder="Da" />
          <Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} placeholder="A" />
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <h3 className="font-display text-xl font-bold mb-3">Top 10 — {groupBy === "employee" ? "Dipendenti" : "Articoli"}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{fontSize: 11}} interval={0} angle={-25} textAnchor="end" height={80} />
              <YAxis tick={{fontSize: 11}} />
              <Tooltip />
              <Bar dataKey="qty" fill="#0067b2" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-6">
          <h3 className="font-display text-xl font-bold mb-3">Dettaglio</h3>
          <div className="divide-y divide-slate-200">
            {rows.length === 0 && <p className="text-sm text-slate-500 text-center py-8">Nessun dato.</p>}
            {rows.map(r => (
              <div key={r.key} className="py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold">{r.label}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{r.deliveries} consegne</Badge>
                    <Badge className="bg-orange-500 text-white border-0">{r.total_qty} pezzi</Badge>
                  </div>
                </div>
                {r.items && Object.entries(r.items).length > 0 && (
                  <div className="text-xs text-slate-500 mt-1">
                    {Object.entries(r.items).map(([k,v]) => `${k}: ${v}`).join(" · ")}
                  </div>
                )}
                {r.by_size && Object.entries(r.by_size).length > 0 && (
                  <div className="text-xs text-slate-500 mt-1">
                    {Object.entries(r.by_size).map(([k,v]) => `Taglia ${k}: ${v}`).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
